<script setup lang="ts">
  import { ref, onMounted, watch, nextTick } from 'vue'
  import { Modal, Card, Button, Spin, Input, Checkbox } from 'ant-design-vue'
  import { PlusOutlined, SearchOutlined, WarningFilled } from '@ant-design/icons-vue'
  import { CustomReportSchema } from '../models/CustomReportSchema'

  const props = defineProps<{
    visible: boolean
    customReportSchemas: Array<CustomReportSchema>
  }>()

  const emit = defineEmits<{
    'update:visible': [value: boolean]
    'customReport-selected': [customReport: CustomReportSchema]
  }>()

  const availableCustomReports = ref<CustomReportSchema[]>([])
  const filteredCustomReports = ref<CustomReportSchema[]>([])
  const showHiddenCustomReports = ref<boolean>(false)
  const loadingCustomReports = ref<boolean>(false)
  const hasErrorLoadingCustomReports = ref<boolean>(false)
  const searchText = ref<string>('')
  const searchInputRef = ref<InstanceType<typeof Input> | null>(null)

  // Group custom reports by category
  const groupedCustomReports = ref<Record<string, CustomReportSchema[]>>({})

  const onSearchTextChanged = (event: InputEvent): void => {
    const target = event.target as HTMLInputElement
    const value = target.value
    const search = value?.toLowerCase().trim()

    if (search?.length > 0) {
      filteredCustomReports.value = availableCustomReports.value.filter(
        (schema: CustomReportSchema) => {
          return (
            (schema.description && schema.description.toLowerCase().includes(search)) ||
            (schema.display_name && schema.display_name.toLowerCase().includes(search))
          )
        }
      )
    } else {
      filteredCustomReports.value = [...availableCustomReports.value]
    }
    groupCustomReportsByCategory()
  }

  const groupCustomReportsByCategory = () => {
    const grouped: Record<string, CustomReportSchema[]> = {}
    filteredCustomReports.value.forEach((calc) => {
      if (!grouped[calc.category]) {
        grouped[calc.category] = []
      }
      grouped[calc.category].push(calc)
    })
    groupedCustomReports.value = grouped
  }

  const selectCustomReport = (customReport: CustomReportSchema) => {
    emit('customReport-selected', customReport)
    // Keep dialog open to allow adding multiple custom reports easily
  }

  const handleCancel = () => {
    emit('update:visible', false)
  }

  onMounted(() => {
    resetAvailableCustomReports()
  })

  const handleVisibilityChange = (visible: boolean) => {
    if (visible) {
      resetAvailableCustomReports()
      nextTick(() => {
        // reset the search model (don't overwrite the Input ref)
        searchText.value = ''

        // try to call component focus() first
        const comp = searchInputRef.value as any
        if (comp?.focus && typeof comp.focus === 'function') {
          comp.focus()
          return
        }

        // fallback: find the native input element and focus it
        const el = comp?.$el ?? comp
        const nativeInput = (el as HTMLElement | null)?.querySelector?.(
          'input'
        ) as HTMLInputElement | null
        nativeInput?.focus()
      })
    }
  }

  const resetAvailableCustomReports = () => {
    availableCustomReports.value = props.customReportSchemas
    filteredCustomReports.value = props.customReportSchemas
    groupCustomReportsByCategory()
  }

  watch(
    () => props.visible,
    (newValue: boolean) => {
      handleVisibilityChange(newValue)
    }
  )

  watch(
    () => props.customReportSchemas,
    (newItems) => {
      availableCustomReports.value = [...newItems]
      filteredCustomReports.value = [...newItems]
      groupCustomReportsByCategory()
    }
  )

  defineExpose({
    availableCustomReports
  })
</script>

<template>
  <Modal
    :open="visible"
    title="Add Custom Report"
    width="1000px"
    :bodyStyle="{ minHeight: '60vh' }"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="customReport-selection-content">
      <!-- Search Bar -->
      <div class="mb-4 flex items-center pr-2">
        <Input
          ref="searchInputRef"
          v-model:value="searchText"
          placeholder="Search custom reports by name or description..."
          size="large"
          @input="onSearchTextChanged"
        >
          <template #prefix>
            <SearchOutlined />
          </template>
        </Input>
        <Checkbox
          class="ml-2 flex-shrink-0 whitespace-nowrap"
          v-model:checked="showHiddenCustomReports"
        >
          Show All Custom Reports
        </Checkbox>
      </div>

      <!-- Loading State -->
      <div v-if="loadingCustomReports" class="py-8 text-center">
        <Spin size="large" />
        <p class="mt-2">Loading available custom reports...</p>
      </div>

      <!-- Custom Reports List -->
      <div v-else id="customReportsListContainer" class="max-h-[60vh] overflow-y-auto">
        <div
          v-if="!hasErrorLoadingCustomReports"
          v-for="(customReports, category) in groupedCustomReports"
          :key="category"
          class="mb-6"
        >
          <!-- Only show category if it has custom reports (after filtering) -->
          <template
            v-if="
              customReports.filter((x) => (showHiddenCustomReports ? x : x.is_visible === true))
                .length > 0
            "
          >
            <h3 class="mb-3 border-b pb-1 text-lg font-semibold">
              {{ category }}
              <span v-if="searchText.trim()" class="ml-2 text-sm font-normal">
                ({{ customReports.length }}
                {{ customReports.length === 1 ? 'custom report' : 'custom reports' }})
              </span>
            </h3>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Card
                v-for="customReport in customReports.filter((x) =>
                  showHiddenCustomReports ? x : x.is_visible === true
                )"
                :key="customReport.type"
                class="cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md"
                size="small"
                @click="selectCustomReport(customReport)"
              >
                <div class="p-2">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <h4 class="mb-1 text-sm font-medium">{{ customReport.display_name }}</h4>
                      <p class="mb-2 text-xs dark:text-[var(--white-50)]">
                        {{ customReport.description }}
                      </p>
                      <div class="flex items-center text-xs">
                        <span
                          class="rounded bg-blue-100 px-2 py-1 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        >
                          {{ customReport.fields.length }} field{{
                            customReport.fields.length !== 1 ? 's' : ''
                          }}
                        </span>
                      </div>
                    </div>
                    <PlusOutlined class="ml-2 text-blue-500" />
                  </div>
                </div>
              </Card>
            </div>
          </template>
        </div>
        <div
          v-else-if="hasErrorLoadingCustomReports"
          class="mt-1 mt-[20vh] flex justify-center text-red-400"
        >
          <WarningFilled class="mr-2 text-2xl" />
          An error was encountered loading custom reports.
        </div>
        <!-- No Results -->
        <div
          v-if="!hasErrorLoadingCustomReports && filteredCustomReports.length === 0"
          class="py-8 text-center"
        >
          <p>
            {{
              searchText.trim()
                ? 'No custom reports found matching your search.'
                : 'No custom reports available.'
            }}
          </p>
          <p v-if="searchText.trim()" class="mt-1 text-xs">
            Try adjusting your search terms or clear the search to browse all custom reports.
          </p>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="mt-6 flex items-center justify-between border-t pt-4">
        <div class="text-sm">
          Click on any custom report above to add it to your custom reports pipeline
        </div>
        <div class="flex gap-2">
          <Button @click="handleCancel">Cancel</Button>
          <Button type="primary" @click="handleCancel">Close</Button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
  .customReport-selection-content {
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  #customReportsListContainer {
    flex: 1;
  }
  #customReportsListContainer::-webkit-scrollbar {
    width: 6px;
  }
  #customReportsListContainer::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  #customReportsListContainer::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  #customReportsListContainer::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>